import { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';
import { IHttpService, HttpService } from '../../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../../services/notifierService';
import { QuarterlyPeriodsResponseModel } from '../../../../models/quarterlyPeriods/QuarterlyPeriodsResponseModel';
import { IServiceShareFiltersModel } from '../../../../models/serviceShareInput/ServiceShareFiltersModel';
import { DropdownOption } from '../../../../models/common/DropdownOption';
import { PaginatorPageState } from 'primereact/paginator';
import { AutoCompleteChangeParams, AutoCompleteCompleteMethodParams } from 'primereact/autocomplete';

export const useServiceShareInputFilters = () => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const [isFiltersReady, setIsFiltersReady] = useState<boolean>(false);

	const arrangeYearsOptions = (years: number[]) => {
		const currentYear = new Date().getFullYear();

		const updatedYears = [
			currentYear,
			...years.filter((year) => year !== currentYear)
		];
		return updatedYears;
	};

	const {
		control,
		watch,
		reset,
		setValue,
		formState: { isValid, errors }
	} = useForm<IServiceShareFiltersModel>({
		defaultValues: {
			filterValuesSelected: {
				quarterlyPeriodQuarter: undefined,
				quarterlyPeriodYear: undefined,
				distributorOrEntity: undefined,
				brandsIds: undefined,
				channel: undefined,
				countryId: undefined,
				page: 1,
				sortBy: 'creationDate',
				order: 'desc'
			},
			filterValuesOptions: {
				quartersOptions: [],
				yearsOptions: [],
				brandsOptions: [],
				channelsOptions: [],
				countriesOptions: []
			}
		}
	});

	const formValues = watch();

	const [cookies, setCookie, removeCookie] = useCookies([
		'serviceShareInputQuarter',
		'serviceShareInputYear',
		'serviceShareInputBrands',
		'serviceShareInputChannel',
		'serviceShareInputCountry'
	]);

	const handleFetchFiltersOptionsData = async () => {
		await httpService
			.get<QuarterlyPeriodsResponseModel>(
				`quarterlyPeriods/dropdown`,
				undefined
			)
			.then((options) => {
				setValue(
					'filterValuesOptions.quartersOptions',
					options.quarters
				);

				const sortedYears = arrangeYearsOptions(options.years);

				setValue('filterValuesOptions.yearsOptions', sortedYears);

				setValue(
					'filterValuesSelected.quarterlyPeriodQuarter',
					cookies.serviceShareInputQuarter
				);
				setValue(
					'filterValuesSelected.quarterlyPeriodYear',
					cookies.serviceShareInputYear
				);

				if (!cookies.serviceShareInputYear) {
					const currentYear = new Date().getFullYear();

					setValue(
						'filterValuesSelected.quarterlyPeriodYear',
						currentYear
					);
				}
			})
			.catch((err) => {
				notifierService.error();
			});

		await httpService
			.get<DropdownOption[]>(
				`countries/dropdown?searchAll=true`,
				undefined
			)
			.then((options) => {
				setValue('filterValuesOptions.countriesOptions', options);

				if (
					options
						.map((x) => x.value)
						.includes(cookies.serviceShareInputCountry)
				) {
					setValue(
						'filterValuesSelected.countryId',
						cookies.serviceShareInputCountry
					);
				}
			})
			.catch((err) => {
				notifierService.error();
			});

		await httpService
			.get<DropdownOption[]>(
				`serviceShareInputs/channelsDropdown`,
				undefined
			)
			.then((options) => {
				setValue('filterValuesOptions.channelsOptions', options);

				if (
					options
						.map((x) => x.value)
						.includes(cookies.serviceShareInputChannel)
				) {
					setValue(
						'filterValuesSelected.channel',
						cookies.serviceShareInputChannel
					);
				}
			})
			.catch((err) => {
				notifierService.error();
			});

		await httpService
			.get<DropdownOption[]>(`brands/dropdown`, undefined)
			.then((options) => {
				setValue('filterValuesOptions.brandsOptions', options);

				const brandsFromCookies = cookies.serviceShareInputBrands;

				if (!Array.isArray(brandsFromCookies)) {
					return;
				}

				const brandsOptionsIds = options.map((x) => x.value);

				const validIds = brandsFromCookies.filter((id: number) =>
					brandsOptionsIds.includes(id)
				);

				if (validIds) {
					setValue('filterValuesSelected.brandsIds', validIds);
				}
			})
			.catch((err) => {
				notifierService.error();
			});

		await httpService
			.get<string[]>(`serviceShareInputs/distributors`, undefined)
			.then((options) => {
				setValue('filterValuesOptions.distributorsOptions', options);
			})
			.catch((err) => {
				notifierService.error();
			});

		setIsFiltersReady(true);
	};

	const handlePageChange = (event: PaginatorPageState) => {
		setValue('filterValuesSelected.page', event.page + 1);
	};

	const clearFilters = () => {
		setValue('filterValuesSelected.quarterlyPeriodQuarter', undefined);
		setValue('filterValuesSelected.quarterlyPeriodYear', undefined);
		setValue('filterValuesSelected.distributorOrEntity', undefined);
		setValue('filterValuesSelected.brandsIds', undefined);
		setValue('filterValuesSelected.channel', undefined);
		setValue('filterValuesSelected.countryId', undefined);
		removeCookie('serviceShareInputQuarter');
		removeCookie('serviceShareInputYear');
		removeCookie('serviceShareInputBrands');
		removeCookie('serviceShareInputChannel');
		removeCookie('serviceShareInputCountry');
	};

	const searchDistributors = (e: AutoCompleteCompleteMethodParams) => {
		const results =
			formValues.filterValuesOptions.distributorsOptions.filter((item) =>
				item.toLowerCase().includes(e.query.toLowerCase())
			);

		setValue('filterValuesOptions.selectedDistributorsOptions', results);
	};

	useEffect(() => {
		handleFetchFiltersOptionsData();
	}, []);

	return {
		isFiltersReady,
		filtersControl: control,
		serviceShareInputFilters: formValues,
		handlePageChange,
		clearFilters,
		searchDistributors,
	};
};
